import type {
  AttributeRequest,
  AttributeResponse,
  CodeResponse,
  MyReferrerResponse,
  StatsResponse,
} from "@src/features/referral/types"

const REQUEST_TIMEOUT_MS = 15_000

const ROUTES = {
  code: "/api/referrals/code",
  attribute: "/api/referrals/attribute",
  stats: "/api/referrals/stats",
  myReferrer: "/api/referrals/my-referrer",
} as const

function apiUrl(path: string): string {
  const baseUrl =
    process.env.NEXT_PUBLIC_BASE_URL ??
    (typeof window !== "undefined" ? window.location.origin : "")
  return baseUrl ? new URL(path, baseUrl).toString() : path
}

async function withTimeout<T>(promise: Promise<T>): Promise<T> {
  let timeoutId: ReturnType<typeof setTimeout> | undefined
  const timeout = new Promise<never>((_, reject) => {
    timeoutId = setTimeout(
      () => reject(new Error("Request timed out")),
      REQUEST_TIMEOUT_MS
    )
  })

  try {
    return await Promise.race([promise, timeout])
  } finally {
    if (timeoutId) clearTimeout(timeoutId)
  }
}

async function jsonFetch<T>(input: string, init?: RequestInit): Promise<T> {
  const url = apiUrl(input)
  const res = await withTimeout(
    fetch(url, {
      ...init,
      cache: "no-store",
    })
  )
  if (!res.ok) throw new Error(`${input} → ${res.status}`)
  return (await res.json()) as T
}

export const fetchReferralCode = () => jsonFetch<CodeResponse>(ROUTES.code)
export const generateReferralCode = () =>
  jsonFetch<CodeResponse>(ROUTES.code, { method: "POST" })
export const fetchReferralStats = () => jsonFetch<StatsResponse>(ROUTES.stats)
export const fetchMyReferrer = () =>
  jsonFetch<MyReferrerResponse>(ROUTES.myReferrer)

// Throws on any body missing `attributed` so 401s don't look transient.
export async function attributeReferralCode(
  referralCode: string
): Promise<AttributeResponse> {
  const body: AttributeRequest = { referralCode }
  const res = await withTimeout(
    fetch(apiUrl(ROUTES.attribute), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
      cache: "no-store",
    })
  )
  const parsed: unknown = await res.json().catch(() => null)
  if (
    parsed &&
    typeof parsed === "object" &&
    "attributed" in parsed &&
    typeof (parsed as { attributed: unknown }).attributed === "boolean"
  ) {
    return parsed as AttributeResponse
  }
  throw new Error(`${ROUTES.attribute} → ${res.status}`)
}
